import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Wand2,
  FileText,
  BookOpen,
  Search,
  Settings,
  ChevronLeft,
  ChevronRight,
  Zap,
  Shield,
  UserCheck,
  LogOut,
  HelpCircle,
  FileWarning,
  ClipboardCheck,
  Share2,
  Megaphone,
  Calculator,
  Fingerprint,
  Star,
  MessageSquare,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { getDaysRemaining } from '@/lib/utils'
import { APP_NAME } from '@/lib/constants'

interface NavItem {
  to: string
  icon: React.ElementType
  label: string
  adminOnly?: boolean
}

const navItems: NavItem[] = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/products', icon: Package, label: 'Products' },
  { to: '/orders', icon: ShoppingCart, label: 'Orders' },
  { to: '/customers', icon: Users, label: 'Customers' },
  { to: '/wizard', icon: Wand2, label: 'Setup Wizard' },
  { to: '/content', icon: FileText, label: 'Content' },
  { to: '/content-library', icon: BookOpen, label: 'Library' },
  { to: '/research', icon: Search, label: 'Research' },
  { to: '/va-guide', icon: Users, label: 'VA Guide' },
  { to: '/faq-generator', icon: HelpCircle, label: 'FAQ Generator' },
  { to: '/chargeback', icon: FileWarning, label: 'Chargeback' },
  { to: '/social-generator', icon: Share2, label: 'Social Generator' },
  { to: '/product-optimizer', icon: Search, label: 'SEO Optimizer' },
  { to: '/ad-remixer', icon: Megaphone, label: 'Ad Remixer' },
  { to: '/profit-calculator', icon: Calculator, label: 'Profit Calc' },
  { to: '/branding', icon: Fingerprint, label: 'Branding' },
  { to: '/influencer-outreach', icon: Star, label: 'Influencers' },
  { to: '/review-generator', icon: MessageSquare, label: 'Reviews' },
  { to: '/store-checklist', icon: ClipboardCheck, label: 'Store Checklist' },
  { to: '/settings', icon: Settings, label: 'Settings' },
  { to: '/admin', icon: Shield, label: 'Admin', adminOnly: true },
]

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUIStore()
  const { user, logout } = useAuthStore()
  const location = useLocation()

  const daysLeft = user?.trialEndsAt ? getDaysRemaining(user.trialEndsAt) : 0
  const isTrialing = user?.subscriptionStatus === 'trial'

  const visibleItems = navItems.filter((item) => !item.adminOnly || user?.isAdmin)

  return (
    <motion.aside
      animate={{ width: sidebarCollapsed ? 64 : 240 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className="hidden md:flex flex-col bg-surface border-r border-border h-screen sticky top-0 z-40 overflow-hidden"
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-border">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
          <Zap className="w-4 h-4 text-white" />
        </div>
        <AnimatePresence>
          {!sidebarCollapsed && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="font-bold text-foreground text-lg whitespace-nowrap"
            >
              {APP_NAME}
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => {
          const Icon = item.icon
          const isActive = location.pathname.startsWith(item.to)
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150',
                'text-sm font-medium',
                isActive
                  ? 'bg-primary/15 text-primary'
                  : 'text-muted hover:bg-surface-2 hover:text-foreground'
              )}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <AnimatePresence>
                {!sidebarCollapsed && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="whitespace-nowrap"
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </NavLink>
          )
        })}
      </nav>

      {/* Trial Banner */}
      <AnimatePresence>
        {!sidebarCollapsed && isTrialing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mx-3 mb-3 p-3 rounded-xl bg-warning/10 border border-warning/30"
          >
            <p className="text-xs font-semibold text-warning">{daysLeft} days left in trial</p>
            <p className="text-xs text-muted mt-0.5">Upgrade to keep access</p>
            <NavLink
              to="/settings"
              className="text-xs font-medium text-warning hover:underline mt-1 block"
            >
              View plans →
            </NavLink>
          </motion.div>
        )}
      </AnimatePresence>

      {/* User & Collapse */}
      <div className="border-t border-border p-3 space-y-1">
        <div className={cn('flex items-center gap-2 px-2 py-2', sidebarCollapsed && 'justify-center')}>
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <UserCheck className="w-4 h-4 text-primary" />
          </div>
          <AnimatePresence>
            {!sidebarCollapsed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 min-w-0"
              >
                <p className="text-xs font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted truncate">{user?.email}</p>
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {!sidebarCollapsed && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => logout()}
                className="text-muted hover:text-error transition-colors"
                title="Log out"
              >
                <LogOut className="w-4 h-4" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        <button
          onClick={toggleSidebar}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-muted hover:bg-surface-2 hover:text-foreground transition-colors text-sm"
        >
          {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : (
            <>
              <ChevronLeft className="w-4 h-4" />
              <span className="text-xs">Collapse</span>
            </>
          )}
        </button>
      </div>
    </motion.aside>
  )
}
